import { Box, HStack, Text, VStack } from '@chakra-ui/react';

import { getPrettyTaxonomyName } from '../../shared/components/utils/roleTaxonomy';
import { useRoleTaxonomyVersions } from '../selection-lists/hooks/useRoleTaxonomyAdapter';
import { useSelectionLists } from '../selection-lists/hooks/useSelectionLists';
import type { SelectionList } from '../selection-lists/types';
import { UserPreferenceKey, type UserPreferences } from './types';

interface PreferencesTooltipContentProps {
  preferences: UserPreferences;
  preferenceVisibility?: (prefKey: UserPreferenceKey) => boolean;
}

const getAdjustmentLabel = (isRaw: boolean) =>
  isRaw ? 'Unadjusted' : 'Adjusted';
const getContingentLabel = (include: boolean) =>
  include ? 'Included' : 'Excluded';
const buildFullRoleLabel = (
  selectionLists: {
    rics_k50?: SelectionList<'rics_k50'>;
    rics_k10?: SelectionList<'rics_k10'>;
  },
  role: UserPreferences[UserPreferenceKey.Taxonomies]['role'] | undefined,
  taxonomies?: { taxonomyId: number; taxonomyName: string }[]
) => {
  if (!role) return '—';
  if (role === 'adaptive') return 'Adaptive';
  if (typeof role === 'object') {
    if ('taxonomyId' in role && role.taxonomyId) {
      const idNum = Number(role.taxonomyId);
      const meta = taxonomies?.find((t) => t.taxonomyId === idNum);
      return getPrettyTaxonomyName(meta?.taxonomyName);
    }
    if ('ricsK50Id' in role && role.ricsK50Id) {
      const idNum = parseInt(role.ricsK50Id, 10);
      const item = selectionLists.rics_k50?.find((i) => i.id === String(idNum));
      const name = item?.longName || item?.shortName || 'Unknown';
      return name;
    }
    if ('ricsK10Id' in role && role.ricsK10Id) {
      const baseId = parseInt(role.ricsK10Id, 10);
      const item = selectionLists.rics_k10?.find(
        (i) => i?.id === String(baseId)
      );
      const name = item?.longName || item?.shortName || 'Unknown';
      return name;
    }
    if ('default' in role && role.default) return 'Default';
  }
  return '—';
};

export function PreferencesTooltipContent({
  preferences,
  preferenceVisibility,
}: PreferencesTooltipContentProps) {
  const selectionLists = useSelectionLists(['rics_k50', 'rics_k10']);
  const { taxonomies } = useRoleTaxonomyVersions();
  return (
    <Box color="white" p={1}>
      <VStack align="stretch" spacing={1}>
        {(!preferenceVisibility ||
          preferenceVisibility(UserPreferenceKey.DataAdjustments)) && (
          <HStack justify="space-between">
            <Text color="white">Data adjustment:</Text>
            <Text color="white" fontWeight="semibold">
              {getAdjustmentLabel(
                preferences[UserPreferenceKey.DataAdjustments]
              )}
            </Text>
          </HStack>
        )}
        {(!preferenceVisibility ||
          preferenceVisibility(UserPreferenceKey.Taxonomies)) && (
          <HStack justify="space-between">
            <Text color="white">Role Taxonomy:</Text>
            <Text color="white" fontWeight="semibold">
              {buildFullRoleLabel(
                selectionLists ?? {},
                preferences[UserPreferenceKey.Taxonomies]?.role,
                taxonomies
              )}
            </Text>
          </HStack>
        )}
        {(!preferenceVisibility ||
          preferenceVisibility(UserPreferenceKey.ContingentWorkers)) && (
          <HStack justify="space-between">
            <Text color="white">Contingent workers:</Text>
            <Text color="white" fontWeight="semibold">
              {getContingentLabel(
                preferences[UserPreferenceKey.ContingentWorkers]
              )}
            </Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
}

export default PreferencesTooltipContent;
